/** The waiting-document annotation of `GET /coverage`, reduced to what a screen needs (ADR-0166).
 *
 * **Nothing here is a state, a count or a colour, and nothing may become one.** A window's state
 * is the census's (`submitted`, `declared_nil`, `missing`, `pending`, `unknown`, `not_expected`)
 * and stays so; a document that *may* cover a window and is waiting for a person is a note
 * beside it. These helpers only find the note. They take no state as input that they return
 * changed, and no function here returns a number a total could be built from.
 *
 * **The windows that may carry a note are the server's list, restated, and restated so the client
 * refuses what the server should not have sent.** The server marks `missing`, `pending`, `unknown`
 * and `declared_nil` only. A note on a `submitted` window would be redundant and one on
 * `not_expected` would claim something is owed where nothing is, so a response that carried either
 * is not rendered, rather than trusted.
 */

import type { CoverageRollup, WaitingDocument } from "../../api/coverageRollup";

/** The census states a waiting-document note may sit beside. Anything else shows no note. */
export const ANNOTATED_STATES: readonly string[] = ["missing", "pending", "unknown", "declared_nil"];

export const mayCarryNote = (state: string): boolean => ANNOTATED_STATES.includes(state);

/** What to call a window in a sentence, from the stream's cadence. A cadence this does not know
 * (`on_event`) is just a window; the screen does not guess a month into existence. */
export function unitWord(cadence: string): string {
  if (cadence === "monthly") return "month";
  if (cadence === "quarterly") return "quarter";
  if (cadence === "annual") return "year";
  return "window";
}

export const windowKey = (sourceId: string, periodStart: string): string => `${sourceId}|${periodStart}`;

/** Waiting documents by `source_id|period_start`, only for windows that may carry a note. */
export function waitingByWindow(rollup: CoverageRollup | undefined): ReadonlyMap<string, readonly WaitingDocument[]> {
  const out = new Map<string, readonly WaitingDocument[]>();
  if (!rollup) return out;
  for (const site of rollup.sites) {
    for (const source of site.sources) {
      for (const window of source.windows) {
        if (window.waiting_documents.length > 0 && mayCarryNote(window.state)) {
          out.set(windowKey(source.source_id, window.period_start), window.waiting_documents);
        }
      }
    }
  }
  return out;
}

export interface Placement {
  facilityName: string;
  periodStart: string;
  periodEnd: string;
  /** The window's own census state, unchanged by the document. */
  state: string;
}

/** Where the server placed one document (the first window that lists it), or `null`.
 *
 * Read from the server's annotation and not re-derived: the uploader's site hint and the closed
 * kind table are the server's, and a second matcher in the browser would be a second opinion about
 * where a document belongs (SEC-05). `null` means "not placed", never "not waiting".
 */
export function placementOf(rollup: CoverageRollup | undefined, documentId: string): Placement | null {
  if (!rollup) return null;
  for (const site of rollup.sites) {
    for (const source of site.sources) {
      for (const window of source.windows) {
        if (
          mayCarryNote(window.state) &&
          window.waiting_documents.some((document) => document.document_id === documentId)
        ) {
          return {
            facilityName: site.facility_name,
            periodStart: window.period_start,
            periodEnd: window.period_end,
            state: window.state,
          };
        }
      }
    }
  }
  return null;
}
